/**
 * CommentsDrawer Component
 *
 * A generic component for displaying and managing comments on any entity.
 * Positioned as a dialog in the bottom-right corner of the screen.
 */

import './CommentsDrawer.scss';

import { GFTDialog } from '@amzn/gft-frontend-commons';
import { useUserContext } from '@context/UserContext';
import SendIcon from '@mui/icons-material/Send';
import { Box, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { createId } from '@paralleldrive/cuid2';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import CommentItem from './CommentItem';
import { CommentsDrawerProps, EnhancedComment } from './CommentsDrawer.types';

const CommentsDrawer = (props: CommentsDrawerProps) => {
  const { open, onClose, comments, onSave, subjectLabel, isEditingDisabled, maxAgeInDays } = props;
  const { username } = useUserContext();
  const [newCommentText, setNewCommentText] = useState('');
  const prevCommentCountRef = useRef((comments || []).length);
  const prevSubjectLabelRef = useRef(subjectLabel);
  const textFieldRef = useRef<HTMLInputElement>(null);

  /**
   * Effect for clearing the new comment text when the drawer is closed.
   */
  useEffect(() => {
    if (!open) {
      setNewCommentText('');
    }
  }, [open]);

  /**
   * Effect for handling auto-scrolling on comments autofocus on comment input.
   */
  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    if (!isNil(comments)) {
      const shouldScroll =
        comments.length > prevCommentCountRef.current || // New comment added
        subjectLabel !== prevSubjectLabelRef.current || // Different target item
        open; // Drawer just opened

      if (shouldScroll) {
        if (comments.length > 0) {
          // Adding timeout to defer auto-scrolling to the next render cycle.
          scrollTimeout = setTimeout(() => {
            const commentsList = document.querySelector('.comments-list');
            if (commentsList) {
              commentsList.scrollTop = commentsList.scrollHeight;
            }
          }, 0);
        }

        // Autofocus on the text field
        if (textFieldRef.current && subjectLabel !== prevSubjectLabelRef.current) {
          textFieldRef.current.focus();
        }
      }
      prevCommentCountRef.current = comments.length;
      prevSubjectLabelRef.current = subjectLabel;
    }

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [comments, subjectLabel, open]);

  /**
   * Effect for handling a new comment to be added
   */
  const handleAddComment = () => {
    if (!newCommentText.trim() || !username) return;

    const newComment: EnhancedComment = {
      __typename: 'Comment',
      commentId: createId(),
      user: username,
      commentDesc: newCommentText.trim(),
      timestamp: new Date().toISOString(),
      metadata: { isPendingSave: true, isPendingDelete: false },
    };

    const updatedCommentsList = !isNil(comments) ? [...comments, newComment] : [newComment];

    onSave(updatedCommentsList);
    setNewCommentText('');
  };

  /**
   * Effect for handling deleting a comment.
   * Updates the target comment's metadata to be pending delete.
   * @param commentId
   */
  const handleDeleteComment = (commentId: string) => {
    if (isNil(comments) || isEmpty(comments)) return;

    const comment = comments.find((c) => c?.commentId === commentId);
    if (!comment) return;

    if (comment.metadata.isPendingSave) {
      console.debug('[handleDeleteComment] isPendingSave:', comment.metadata.isPendingSave);
      // If the comment is pending save (`metadata.isPendingSave === true`), completely remove from the comment list.
      // (i.e.: Never saved to the backend service)
      const updatedCommentsList = comments.filter((c) => c?.commentId !== commentId);
      onSave(updatedCommentsList);
    } else if (comment.metadata.isPendingDelete) {
      console.debug('[handleDeleteComment] isPendingDelete:', comment.metadata.isPendingDelete);
      // If the comment is already pending delete, then Undo the delete action.
      const updatedCommentsList = comments.map((c) =>
        c?.commentId === commentId
          ? {
              ...c,
              metadata: { ...c.metadata, isPendingDelete: false },
            }
          : c,
      );
      onSave(updatedCommentsList);
    } else {
      console.debug('[handleDeleteComment] isPendingDelete:', comment.metadata.isPendingDelete);
      // Otherwise, set isPendingDelete to true and update the comments list.
      // (i.e.: Mark for deletion on the backend service)
      const updatedCommentsList = comments.map((c) =>
        c?.commentId === commentId
          ? {
              ...c,
              metadata: { ...c.metadata, isPendingDelete: true },
            }
          : c,
      );

      onSave(updatedCommentsList);
    }
  };

  /**
   * Effect for handling the closing of the drawer
   */
  const handleClose = () => {
    setNewCommentText('');
    onClose();
  };

  return (
    <GFTDialog
      open={open}
      onClose={handleClose}
      className='comments-drawer'
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      slotProps={{
        root: {
          style: { pointerEvents: 'none' },
        },
        paper: {
          style: { pointerEvents: 'auto' },
        },
      }}
      title={`Comments - ${subjectLabel}`}
    >
      <Box className='comments-list'>
        {isNil(comments) || comments.length === 0 ? (
          <Typography variant='body2' className='no-comments'>
            No comments yet
          </Typography>
        ) : (
          <Stack spacing={0}>
            {comments
              .map((comment, index) =>
                isNil(comment) ? null : (
                  <CommentItem
                    key={comment.commentId}
                    {...comment}
                    onDelete={handleDeleteComment}
                    index={index}
                    isEditingDisabled={isEditingDisabled}
                  />
                ),
              )
              .filter(Boolean)}
          </Stack>
        )}
      </Box>

      <Divider className='divider' />

      <TextField
        inputRef={textFieldRef}
        autoFocus
        fullWidth
        label='Add a Comment'
        value={newCommentText}
        onChange={(e) => setNewCommentText(e.target.value)}
        disabled={isEditingDisabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAddComment();
          }
        }}
        className='comment-input'
        autoComplete='off'
        helperText={`Comments archived after ${maxAgeInDays} days`}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handleAddComment}
                  disabled={!newCommentText.trim() || isEditingDisabled}
                  edge='end'
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
    </GFTDialog>
  );
};

export default CommentsDrawer;
